import { Box, type SxProps, type Theme } from "@mui/material";
import * as React from "react";

interface ToggleSoundButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  sx?: SxProps<Theme>;
  isPlaying: boolean;
}

const ToggleSoundButton = React.forwardRef<
  HTMLButtonElement,
  ToggleSoundButtonProps
>(
  (
    {
      sx,
      isPlaying,
      disabled,
      type = "button",
      className,
      style,
      ...buttonProps
    },
    ref,
  ) => {
    return (
      <Box
        component="button"
        ref={ref}
        type={type}
        disabled={disabled}
        className={`${className} ${isPlaying ? "" : "is-off"}`}
        style={style}
        sx={{
          p: 0,
          m: 0,
          border: 0,
          background: "transparent",
          cursor: "pointer",
          "&:active": {
            outline: "none",
          },
          "&:focus": {
            outline: "none",
          },
          "& svg": {
            transition: "transform 0.15s ease",
            transformOrigin: "center",
            display: "block",
          },
          "&:hover svg": {
            transform: disabled ? "none" : "scale(1.05)",
          },
          "&:active svg": {
            transform: disabled ? "none" : "scale(0.99)",
          },
          "& .off-slash": {
            stroke: "currentColor",
            strokeWidth: 1.25,
            strokeLinecap: "round",
            vectorEffect: "non-scaling-stroke",
            strokeDasharray: 100,
            strokeDashoffset: 100,
            transition: "stroke-dashoffset 150ms ease-out",
          },
          "&.is-off .off-slash": {
            strokeDashoffset: 0,
          },
          "@media (prefers-reduced-motion: reduce)": {
            "& .off-slash": { transition: "none" },
            "&.is-off .off-slash": { strokeDashoffset: 0 },
          },
          ...sx,
        }}
        {...buttonProps}
      >
        <svg
          width="66"
          height="66"
          viewBox="0 0 66 66"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="33" cy="33" r="33" fill="white" />
          <path
            d="M15.3841 36.3397L15.3434 28.6117C15.339 27.7804 16.0115 27.1041 16.8428 27.1038L24.3838 27.1009L34.1416 20.5219C34.2412 20.4548 34.3755 20.5261 34.3755 20.6463V44.3532C34.3755 44.4737 34.2406 44.545 34.141 44.4772L24.3841 37.8318H16.8841C16.0587 37.8318 15.3885 37.1651 15.3841 36.3397Z"
            stroke="black"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            d="M40.2839 32.7041H50.6652"
            stroke="black"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            d="M39.2534 28.1865L46.4232 25.2211"
            stroke="black"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            d="M39.2534 37.2207L46.4232 40.1861"
            stroke="black"
            strokeWidth="1.5"
            strokeLinecap="round"
          />

          <path className="off-slash" d="M19 19 L47 47" pathLength={100} />
        </svg>
      </Box>
    );
  },
);

export default ToggleSoundButton;
